/* Cage Funnel — guided backflow enclosure/cage selector.
 * Steps: Model -> Insulated/Non (side-by-side) -> Size (with dimensions) -> Add-on
 * -> Add to quote (dispatches `quote:add` -> the quote drawer -> /admin estimate).
 *
 * Data-driven from the storefront enclosures-and-cages catalog. The cages are now
 * six GROUPED products (one product per model, with Size + Insulation as variant
 * options), so we match products by handle and expand each product's variants into
 * (insulation, size) buckets using the variants' selectedOptions. Pricing is
 * quote-only (no prices shown). Thumbnails use the product image where Shopify has
 * one so the buyer sees what they're picking at each step.
 */
if (!customElements.get('cage-funnel')) {
  // The six grouped enclosure products, matched by handle. Order here is the
  // order shown in the Model step. Products not in this list (accessories,
  // one-off custom cages) never appear as a model — the "Custom Cage" card
  // routes those to a quote request.
  const MODELS = [
    { key: 'liftoff', handle: 'lift-off-backflow-enclosure', label: 'Lift-Off', desc: 'Expanded-metal lift-off enclosure (BELO / BELOW)' },
    { key: 'aboveground', handle: 'above-ground-backflow-enclosure', label: 'Above-Ground', desc: 'Hinged-door enclosure with 100% access (E1A / E1AW)' },
    { key: 'clamshell', handle: 'clamshell-backflow-cage', label: 'Clamshell', desc: 'Two-door clamshell enclosure (E2A)' },
    { key: 'wallmount', handle: 'insulated-wall-mount-enclosure', label: 'Wall-Mount', desc: 'Insulated wall-mount enclosure (EOSW)' },
    { key: 'access2', handle: '2-door-access-steel-enclosure', label: '2-Door Access Steel', desc: 'Two-door full-access steel enclosure' },
    { key: 'access4', handle: '4-door-access-steel-enclosure', label: '4-Door Access Steel', desc: 'Four-door full-access steel enclosure' },
  ];
  const MODEL_BY_HANDLE = Object.fromEntries(MODELS.map((m) => [m.handle, m]));

  // Curated model beauty shots, served from the app's public folder
  // (app.thebackflowdepot.com/cages-enclosures/…). These take precedence over
  // the Shopify featured image so every card shows a consistent product photo.
  // Insulation-specific: `non` = open expanded-metal screen, `ins` = solid
  // galvanized panel behind the mesh — so the Insulation step, Size cards and
  // summary all show what that build actually looks like. Add more here as
  // photos are uploaded for the remaining models.
  const MODEL_IMAGES = {
    liftoff: { non: '/cages-enclosures/liftoff.jpg', ins: '/cages-enclosures/liftoff-insulated.jpg' },
    aboveground: { non: '/cages-enclosures/aboveground.jpg', ins: '/cages-enclosures/aboveground-insulated.jpg' },
  };

  // Inside-dimension lookup (from PW spec sheets) — W x L x H inches. Keyed by
  // family|size. Only sizes we have confirmed dims for are listed; anything
  // else shows the size label alone (dimensions are confirmed on the quote).
  const DIMS = {
    // Lift-Off is now Size {XS,S,M,L} × Insulation; both builds share the frame
    // footprint per size (formerly the BELO 25/35/41/49 sizes).
    'liftoff|XS': '14"W × 24"L × 27"H', 'liftoff|S': '14"W × 34"L × 27"H',
    'liftoff|M': '14"W × 40"L × 27"H', 'liftoff|L': '14"W × 49"L × 27"H',
    'aboveground|XS': '12"W × 24"L × 26"H', 'aboveground|S': '14"W × 30"L × 24"H',
    'aboveground|M': '17"W × 40"L × 30"H', 'aboveground|L': '20"W × 47"L × 36"H',
  };
  const SIZE_LABELS = { XS: 'Extra Small', S: 'Small', M: 'Medium', L: 'Large', XL: 'Extra Large' };
  // Numeric lift-off sizes sort after the letter sizes; letters follow this order.
  const SIZE_ORDER = { XS: 0, S: 1, M: 2, L: 3, XL: 4 };

  // Per-model, per-insulation spec sheets (PDF), served from the app's public
  // folder (app.thebackflowdepot.com/cages-enclosures/…). Keyed family|non|ins.
  // One sheet covers all sizes of that model/build; shown on the Size step. Add
  // more here as PDFs are uploaded for the remaining models.
  const SPEC_SHEETS = {
    'liftoff|non': '/cages-enclosures/PW-BELO-STD.pdf',
    'liftoff|ins': '/cages-enclosures/PW-BELOW-STD.pdf',
    'aboveground|ins': '/cages-enclosures/PW-E1AW-STD.pdf',
  };

  const PLACEHOLDER = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><rect x="3" y="6" width="18" height="14" rx="1.5"/><path d="M3 10h18M9 6V3.5A1.5 1.5 0 0 1 10.5 2h3A1.5 1.5 0 0 1 15 3.5V6"/></svg>';

  class CageFunnel extends HTMLElement {
    constructor() {
      super();
      this.models = []; // [{ ...MODEL, image, tree: { ins:[], non:[] }, insAvail }]
      this.embedFrames = {}; // size -> { variantId, title } — Lift-Off concrete embed frames
      this.state = { family: null, insulated: null, size: null, addon: false, addonFrame: false };
    }

    connectedCallback() {
      this.apiBase = (this.dataset.apiBase || 'https://app.thebackflowdepot.com').replace(/\/$/, '');
      this.addonVariant = this.dataset.addonVariant || '';
      this.addonTitle = this.dataset.addonTitle || 'Red-head anchor bolts (4-pack)';
      this.innerHTML = '<p class="repair-parts-finder__loading text-center">Loading enclosure options…</p>';
      this.load();
    }

    async load() {
      try {
        const res = await fetch(`${this.apiBase}/api/storefront/parts-finder/products?scope=enclosures-and-cages&category=enclosures-and-cages&limit=100`);
        const data = await res.json();
        this.build(data.products || []);
        this.render();
      } catch (e) {
        this.innerHTML = '<p class="repair-parts-finder__loading text-center">Couldn’t load enclosures. Please call (916) 251-7071 and we’ll help you find the right cage.</p>';
      }
    }

    // Build one entry per known grouped product, expanding its variants into
    // insulated / non-insulated buckets keyed by size.
    build(products) {
      const byKey = {};
      for (const p of products) {
        const model = MODEL_BY_HANDLE[p.handle];
        if (!model) continue; // accessory / custom one-off — not a funnel model
        const curated = MODEL_IMAGES[model.key] || {};
        const abs = (path) => (path ? this.apiBase + path : null);
        // Per-insulation image, falling back to the other insulation's photo,
        // then the Shopify featured image, then a placeholder.
        const insImage = abs(curated.ins) || abs(curated.non) || p.imageUrl || null;
        const nonImage = abs(curated.non) || abs(curated.ins) || p.imageUrl || null;
        const entry = byKey[model.key] || {
          ...model, images: { ins: insImage, non: nonImage }, tree: { ins: [], non: [] },
        };
        for (const v of p.variants || []) {
          if (!v.variantId) continue;
          const size = this.sizeOf(v);
          if (!size) continue;
          const insulated = this.isInsulated(v);
          entry.tree[insulated ? 'ins' : 'non'].push({
            size,
            variantId: v.variantId,
            dims: DIMS[`${model.key}|${size}`] || null,
            image: entry.images[insulated ? 'ins' : 'non'],
            title: p.title,
          });
        }
        byKey[model.key] = entry;
      }
      // Keep only models that resolved to at least one variant, in MODELS order,
      // and pre-sort each bucket's sizes.
      this.models = MODELS
        .map((m) => byKey[m.key])
        .filter((e) => e && (e.tree.ins.length || e.tree.non.length))
        .map((e) => {
          e.tree.ins = this.dedupeSizes(e.tree.ins);
          e.tree.non = this.dedupeSizes(e.tree.non);
          e.insAvail = { ins: e.tree.ins.length > 0, non: e.tree.non.length > 0 };
          return e;
        });

      // Concrete embed frames — one per size, poured-slab anchor. They fit the
      // Lift-Off (BELO/BELOW) sizes identically, so we key them by size and offer
      // them as an optional add-on on the summary step. Matched by handle so a
      // variant-id change never breaks the mapping.
      this.embedFrames = {};
      for (const p of products) {
        if (!/concrete-embed-frame/i.test(p.handle || '')) continue;
        const v = (p.variants || [])[0];
        const size = (p.sizes || [])[0];
        if (v && v.variantId && size) this.embedFrames[size] = { variantId: v.variantId, title: p.title };
      }
    }

    // Prefer the Size option; fall back to the first variant-title segment
    // (single-option products like Wall-Mount / Lift-Off title = the size).
    sizeOf(v) {
      const opt = this.optionValue(v, ['size']);
      if (opt) return opt.trim();
      const first = (v.title || '').split('/')[0].trim();
      return first && first.toLowerCase() !== 'default title' ? first : null;
    }

    // Insulation option wins; else read it off the SKU model code — the trailing
    // "W" (BELOW, E1AW, E2AW, EOSW, FTW, BE4DW) marks the insulated build.
    isInsulated(v) {
      const opt = this.optionValue(v, ['insulation', 'insulated']);
      if (opt) return !/non/i.test(opt);
      const t = (v.title || '').toUpperCase();
      if (/NON[\s-]?INSULAT/.test(t)) return false;
      if (/INSULAT/.test(t)) return true;
      const code = (v.sku || '').replace(/^PW\//i, '').split(/[-\s(]/)[0];
      return /W$/i.test(code);
    }

    optionValue(v, names) {
      for (const o of v.selectedOptions || []) {
        if (names.includes((o.name || '').toLowerCase())) return o.value;
      }
      return null;
    }

    dedupeSizes(list) {
      const seen = new Map();
      for (const p of list) if (!seen.has(p.size)) seen.set(p.size, p);
      return [...seen.values()].sort((a, b) => {
        const ao = SIZE_ORDER[a.size], bo = SIZE_ORDER[b.size];
        if (ao != null && bo != null) return ao - bo;
        if (ao != null) return -1;
        if (bo != null) return 1;
        return String(a.size).localeCompare(String(b.size), undefined, { numeric: true });
      });
    }

    modelFor(key) { return this.models.find((m) => m.key === key) || null; }

    // Spec-sheet PDF URL for the chosen model + insulation, or null if none yet.
    specSheetFor(family, insulated) {
      const path = SPEC_SHEETS[`${family}|${insulated ? 'ins' : 'non'}`];
      return path ? this.apiBase + path : null;
    }

    sizesFor(key, insulated) {
      const m = this.modelFor(key);
      return (m && m.tree[insulated ? 'ins' : 'non']) || [];
    }

    selectedProduct() {
      return this.sizesFor(this.state.family, this.state.insulated).find((p) => p.size === this.state.size) || null;
    }

    setState(patch) { Object.assign(this.state, patch); this.render(); }

    // Selecting a model: if only one insulation exists (e.g. Wall-Mount is
    // insulated-only), auto-pick it and jump straight to Size.
    selectFamily(key) {
      const m = this.modelFor(key);
      let insulated = null;
      if (m && m.insAvail.ins && !m.insAvail.non) insulated = true;
      else if (m && m.insAvail.non && !m.insAvail.ins) insulated = false;
      this.setState({ family: key, insulated, size: null });
    }

    addToQuote() {
      const prod = this.selectedProduct();
      if (!prod) return;
      document.dispatchEvent(new CustomEvent('quote:add', { detail: { variantId: prod.variantId, title: prod.title, handle: this.modelFor(this.state.family)?.handle, quantity: 1 } }));
      if (this.state.addon && this.addonVariant) {
        document.dispatchEvent(new CustomEvent('quote:add', { detail: { variantId: this.addonVariant, title: this.addonTitle, quantity: 1 } }));
      }
      // Concrete embed frame add-on (Lift-Off only, size-matched).
      const frame = this.state.family === 'liftoff' ? this.embedFrames[this.state.size] : null;
      if (this.state.addonFrame && frame) {
        document.dispatchEvent(new CustomEvent('quote:add', { detail: { variantId: frame.variantId, title: frame.title, quantity: 1 } }));
      }
    }

    /* ---- Render ----
       Same markup and classes as the repair parts finder (repair-parts-finder.css):
       sticky step bar, one stage card at a time. Cages are quote-only, so the
       last step is a summary with the add-ons and an Add to quote button. */

    tile(image) {
      return image
        ? `<img class="repair-parts-finder__model-image" src="${this.esc(image)}" alt="" loading="lazy">`
        : `<span class="repair-parts-finder__model-placeholder" aria-hidden="true">${PLACEHOLDER}</span>`;
    }

    sizeLabel(size) {
      return SIZE_LABELS[size] || (/^\d+$/.test(size) ? size + '"' : this.esc(size));
    }

    render() {
      const s = this.state;
      const model = s.family && s.family !== 'custom' ? this.modelFor(s.family) : null;
      const isCustom = s.family === 'custom';
      let step = 1;
      if (s.family) step = 2;
      if (s.family && s.insulated !== null) step = 3;
      if (s.family && s.insulated !== null && s.size) step = 4;
      const insLabel = s.insulated === null ? '' : (s.insulated ? 'Insulated' : 'Non-Insulated');
      // A model with only one build skips the Insulation step, so "back" goes to Model.
      const singleBuild = !!model && !(model.insAvail.ins && model.insAvail.non);

      const sep = '<span class="repair-parts-finder__trail-sep" aria-hidden="true">&rsaquo;</span>';
      const pill = (n, label, value, reachable) =>
        `<button type="button" class="repair-parts-finder__pill${step === n ? ' is-active' : ''}${value ? ' is-set' : ''}" data-goto="${n}"${reachable ? '' : ' disabled'}>${n}. ${label}${value ? `<span>: ${value}</span>` : ''}</button>`;
      const pills = [
        pill(1, 'Model', model ? this.esc(model.label) : (isCustom ? 'Custom' : ''), true),
        pill(2, 'Insulation', isCustom ? '' : insLabel, !!s.family && !isCustom && !singleBuild),
        pill(3, 'Size', s.size ? this.sizeLabel(s.size) : '', s.insulated !== null && !isCustom),
        `<span class="repair-parts-finder__pill repair-parts-finder__pill--static${step === 4 ? ' is-active' : ''}">4. Add-ons</span>`,
      ].join(sep);

      let body = '';
      if (step === 1) {
        const cards = this.models.map((m) => `
          <button type="button" class="repair-parts-finder__model-card repair-parts-finder__model-card--photo" data-family="${m.key}">
            ${this.tile(m.images && (m.images.non || m.images.ins))}
            <span class="repair-parts-finder__model-name">${this.esc(m.label)}</span>
            <span class="repair-parts-finder__tile-desc">${this.esc(m.desc)}</span>
          </button>`);
        cards.push(`
          <button type="button" class="repair-parts-finder__model-card repair-parts-finder__model-card--photo" data-family="custom">
            ${this.tile(null)}
            <span class="repair-parts-finder__model-name">Custom Cage</span>
            <span class="repair-parts-finder__tile-desc">Non-standard size or configuration? We build to spec.</span>
          </button>`);
        body = `<div class="repair-parts-finder__stage-card">
            <div class="repair-parts-finder__stage-heading">
              <h2>Choose the enclosure model</h2>
              <p>Each model comes in several sizes; you'll pick insulation and size next.</p>
            </div>
            <div class="repair-parts-finder__model-grid">${cards.join('')}</div>
          </div>`;
      } else if (isCustom) {
        body = `<div class="repair-parts-finder__stage-card">
            <div class="repair-parts-finder__stage-heading repair-parts-finder__stage-heading--split">
              <div>
                <h2>Custom cage</h2>
                <p>Tell us about the assembly you need to protect — sizes, insulation, location — and we'll build a custom enclosure and send you an estimate.</p>
              </div>
              <button type="button" class="repair-parts-finder__back-link" data-goto="1">Change model</button>
            </div>
            <a class="btn btn--primary" href="/pages/contact">Request a custom cage quote</a>
            <p class="repair-parts-finder__fineprint">Or call <strong>(916) 251-7071</strong>.</p>
          </div>`;
      } else if (step === 2) {
        const avail = model ? model.insAvail : { ins: false, non: false };
        const card = (ins, title, desc) => avail[ins ? 'ins' : 'non'] ? `
          <button type="button" class="repair-parts-finder__model-card repair-parts-finder__model-card--photo" data-insulated="${ins ? '1' : '0'}">
            ${this.tile(model && model.images && model.images[ins ? 'ins' : 'non'])}
            <span class="repair-parts-finder__model-name">${title}</span>
            <span class="repair-parts-finder__tile-desc">${desc}</span>
            <span class="repair-parts-finder__tile-count">Pricing on quote</span>
          </button>` : '';
        body = `<div class="repair-parts-finder__stage-card">
            <div class="repair-parts-finder__stage-heading repair-parts-finder__stage-heading--split">
              <div>
                <h2>Insulated or not? <em>${model ? this.esc(model.label) : ''}</em></h2>
                <p>Insulated enclosures protect the assembly from freezing.</p>
              </div>
              <button type="button" class="repair-parts-finder__back-link" data-goto="1">Change model</button>
            </div>
            <div class="repair-parts-finder__model-grid repair-parts-finder__model-grid--two">
              ${card(false, 'Non-Insulated', 'Open expanded-metal screen — ventilation &amp; security.')}
              ${card(true, 'Insulated', 'Galvanized panel + 1" bonded foam — freeze protection.')}
            </div>
          </div>`;
      } else if (step === 3) {
        const sizes = this.sizesFor(s.family, s.insulated);
        const spec = this.specSheetFor(s.family, s.insulated);
        const specLink = spec
          ? `<a class="repair-parts-finder__spec" href="${this.esc(spec)}" target="_blank" rel="noopener"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6M9 13h6M9 17h6"/></svg>View the ${model ? this.esc(model.label) : ''} spec sheet (PDF)</a>`
          : '';
        body = `<div class="repair-parts-finder__stage-card">
            <div class="repair-parts-finder__stage-heading repair-parts-finder__stage-heading--split">
              <div>
                <h2>What size? <em>${model ? this.esc(model.label) : ''} &middot; ${insLabel}</em></h2>
                <p>Inside dimensions are width × length × height. Measure your assembly with a little room to spare.</p>
              </div>
              <button type="button" class="repair-parts-finder__back-link" data-goto="${singleBuild ? 1 : 2}">${singleBuild ? 'Change model' : 'Change insulation'}</button>
            </div>
            ${specLink}
            <div class="repair-parts-finder__model-grid">${sizes.map((p) => `
              <button type="button" class="repair-parts-finder__model-card repair-parts-finder__model-card--photo" data-size="${this.esc(p.size)}">
                ${this.tile(p.image)}
                <span class="repair-parts-finder__model-name">${this.sizeLabel(p.size)}</span>
                <span class="repair-parts-finder__tile-desc">${p.dims || 'Dimensions confirmed on quote'}</span>
              </button>`).join('')}</div>
          </div>`;
      } else if (step === 4) {
        const prod = this.selectedProduct();
        // Concrete embed frame — Lift-Off only, matched to the chosen size.
        const frame = s.family === 'liftoff' ? this.embedFrames[s.size] : null;
        const frameAddon = frame ? `
          <label class="repair-parts-finder__addon">
            <input type="checkbox" data-addon-frame ${s.addonFrame ? 'checked' : ''}>
            <span><strong>Add a concrete embed frame</strong><small>Anchors the enclosure into a poured concrete slab. Sized to match — fits both insulated &amp; non-insulated.</small></span>
          </label>` : '';
        const chips = [model ? this.esc(model.label) : '', this.sizeLabel(s.size), insLabel, prod && prod.dims ? prod.dims : ''].filter(Boolean);
        const image = prod && prod.image
          ? `<img src="${this.esc(prod.image)}" alt="" loading="lazy">`
          : `<span class="repair-parts-finder__model-placeholder" aria-hidden="true">${PLACEHOLDER}</span>`;
        body = `<div class="repair-parts-finder__stage-card">
            <div class="repair-parts-finder__stage-heading repair-parts-finder__stage-heading--split">
              <div>
                <h2>Add-ons and quote</h2>
                <p>Pick any extras, then add it to your quote.</p>
              </div>
              <button type="button" class="repair-parts-finder__back-link" data-goto="3">Change size</button>
            </div>
            <div class="repair-parts-finder__summary">
              <div class="repair-parts-finder__summary-image">${image}</div>
              <div class="repair-parts-finder__summary-body">
                <p class="repair-parts-finder__summary-title">${prod ? this.esc(prod.title) : ''}</p>
                <div class="repair-parts-finder__chips">${chips.map((c) => `<span>${c}</span>`).join('')}</div>
                ${this.addonVariant ? `<label class="repair-parts-finder__addon">
                  <input type="checkbox" data-addon ${s.addon ? 'checked' : ''}>
                  <span><strong>Add ${this.esc(this.addonTitle.charAt(0).toLowerCase() + this.addonTitle.slice(1))}</strong><small>Recommended for concrete-pad mounting.</small></span>
                </label>` : ''}
                ${frameAddon}
                <button type="button" class="btn btn--primary" data-add-to-quote>Add to quote</button>
                <p class="repair-parts-finder__fineprint">Pricing comes on your quote — a backflow specialist confirms availability, lead time and price, then emails you an estimate to approve.</p>
              </div>
            </div>
          </div>`;
      }

      const tools = step > 1
        ? '<div class="repair-parts-finder__tools"><button type="button" class="repair-parts-finder__reset" data-goto="1">Start over</button></div>'
        : '';

      this.innerHTML = `
        <div class="repair-parts-finder__toolbar">
          <div class="repair-parts-finder__trail" role="group" aria-label="Your enclosure">${pills}</div>
          ${tools}
        </div>
        ${body}
        <p class="repair-parts-finder__help">Not sure? <a href="/collections/enclosures-and-cages">Browse all enclosures</a> or call <strong>(916) 251-7071</strong>.</p>`;
      this.bind();
    }

    bind() {
      this.querySelectorAll('[data-family]').forEach((el) => el.addEventListener('click', () => this.selectFamily(el.dataset.family)));
      this.querySelectorAll('[data-insulated]').forEach((el) => el.addEventListener('click', () => this.setState({ insulated: el.dataset.insulated === '1', size: null })));
      this.querySelectorAll('[data-size]').forEach((el) => el.addEventListener('click', () => this.setState({ size: el.dataset.size })));
      this.querySelector('[data-addon]')?.addEventListener('change', (e) => { this.state.addon = e.target.checked; });
      this.querySelector('[data-addon-frame]')?.addEventListener('change', (e) => { this.state.addonFrame = e.target.checked; });
      this.querySelector('[data-add-to-quote]')?.addEventListener('click', () => this.addToQuote());
      this.querySelectorAll('[data-goto]:not([disabled])').forEach((el) => el.addEventListener('click', () => {
        const n = Number(el.dataset.goto);
        if (n === 1) this.setState({ family: null, insulated: null, size: null });
        else if (n === 2) this.setState({ insulated: null, size: null });
        else if (n === 3) this.setState({ size: null });
      }));
    }

    esc(s) { return String(s ?? '').replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;'); }
  }
  customElements.define('cage-funnel', CageFunnel);
}
